// Service for managing recipients in localStorage

const RECIPIENTS_STORAGE_KEY = 'docplace_recipients';

// Predefined color palette for recipients - alternated primary colors
const RECIPIENT_COLORS = [
  '#F44336', // Red
  '#2196F3', // Blue
  '#4CAF50', // Green
  '#FF9800', // Orange
  '#9C27B0', // Purple
  '#00BCD4', // Cyan
  '#FFEB3B', // Yellow
  '#E91E63', // Pink
  '#009688', // Teal
  '#FF5722', // Deep Orange
  '#673AB7', // Deep Purple
  '#8BC34A', // Light Green
  '#FFC107', // Amber
  '#3F51B5', // Indigo
  '#03A9F4', // Light Blue
  '#CDDC39', // Lime
];

// Generate a unique color for a recipient based on their ID
const getRecipientColor = (recipientId) => {
  if (!recipientId) return RECIPIENT_COLORS[0];
  
  // Use a simple hash function to get a consistent color for each recipient
  let hash = 0;
  for (let i = 0; i < recipientId.length; i++) {
    const char = recipientId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  const colorIndex = Math.abs(hash) % RECIPIENT_COLORS.length;
  return RECIPIENT_COLORS[colorIndex];
};

export const recipientsService = {
  // Save recipients to localStorage
  saveRecipients: (recipients) => {
    try {
      localStorage.setItem(RECIPIENTS_STORAGE_KEY, JSON.stringify(recipients));
      return true;
    } catch (error) {
      console.error('Error saving recipients to localStorage:', error);
      return false;
    }
  },

  // Get recipients from localStorage
  getRecipients: () => {
    try {
      const stored = localStorage.getItem(RECIPIENTS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error getting recipients from localStorage:', error);
      return [];
    }
  },

  // Add a new recipient
  addRecipient: (recipient) => {
    try {
      const recipients = recipientsService.getRecipients();
      const newRecipient = {
        id: Date.now().toString(), // Simple ID generation
        ...recipient,
        createdAt: new Date().toISOString()
      };
      recipients.push(newRecipient);
      recipientsService.saveRecipients(recipients);
      return newRecipient;
    } catch (error) {
      console.error('Error adding recipient:', error);
      return null;
    }
  },

  // Update a recipient
  updateRecipient: (id, updates) => {
    try {
      const recipients = recipientsService.getRecipients();
      const index = recipients.findIndex(r => r.id === id);
      if (index !== -1) {
        recipients[index] = { ...recipients[index], ...updates };
        recipientsService.saveRecipients(recipients);
        return recipients[index];
      }
      return null;
    } catch (error) {
      console.error('Error updating recipient:', error);
      return null;
    }
  },

  // Delete a recipient
  deleteRecipient: (id) => {
    try {
      const recipients = recipientsService.getRecipients();
      const filtered = recipients.filter(r => r.id !== id);
      recipientsService.saveRecipients(filtered);
      return true;
    } catch (error) {
      console.error('Error deleting recipient:', error);
      return false;
    }
  },

  // Clear all recipients
  clearRecipients: () => {
    try {
      localStorage.removeItem(RECIPIENTS_STORAGE_KEY);
      return true;
    } catch (error) {
      console.error('Error clearing recipients:', error);
      return false;
    }
  },

  // Get color for a recipient
  getRecipientColor: getRecipientColor
};

export default recipientsService;
